import { CommonModule } from "@angular/common";
import {
  Component,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "app-bar-chart",
  templateUrl: "./bar-chart.component.html",
  styleUrls: ["./bar-chart.component.scss"],
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule],
})
export class BarChartComponent implements OnChanges, AfterViewInit {
  @Output() dataEvent = new EventEmitter<string>();
  @Input() data: { label: string; count: number }[] = [];
  @Input() barColors: { [key: string]: string } = {};
  @Input() defaultBarColor: string = "#3B82F6";
  @Input() barAlign: string = "justify-evenly";
  @Input() barWidth: string = "40px";
  @Input() chartLabel: any;
  @Input() dynamicWidth: boolean = false;

  chartData: any;

  @ViewChild("chartContainer", { static: false })
  chartContainer?: ElementRef<HTMLDivElement>;

  maxYValue: number = 0;
  yAxisLabels: number[] = [];
  isAllZero: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] || changes["dynamicWidth"]) {
      this.prepareChart();
    }
  }

  ngAfterViewInit(): void {
    if (this.dynamicWidth) {
      this.prepareChart();
    }
  }

  prepareChart() {
    if (!this.data) return;

    let counts = this.data.map((d) => d.count);
    this.isAllZero = counts.every((v) => v === 0);

    if (this.isAllZero) {
      this.data = this.data.map((item) => ({
        ...item,
        count: this.getRandomDummyValue(),
      }));
      counts = this.data.map((d) => d.count);
    }

    this.maxYValue = Math.max(...counts, 0);

    const numberOfSteps = 4;
    const stepSize = this.maxYValue / numberOfSteps;

    this.yAxisLabels = Array.from({ length: 5 }, (_, i) =>
      Math.round(this.maxYValue - i * stepSize)
    );

    // Auto bar width adjustment
    if (this.dynamicWidth && this.chartContainer) {
      setTimeout(() => {
        const containerWidth =
          this.chartContainer?.nativeElement.offsetWidth || 0;
        const totalBars = this.data.length || 1;
        const gap = 100;
        const width = Math.floor(
          (containerWidth - gap * (totalBars - 1)) / totalBars
        );
        const adjustedWidth = Math.min(width - 15, 88);
        this.barWidth = adjustedWidth > 0 ? `${adjustedWidth}px` : "40px";
      }, 500);
    }
  }

  getBarColor(label: string): string {
    if (this.isAllZero) return "#D1D5DB"; // light grey dummy bar
    return this.barColors[label] || this.defaultBarColor;
  }

  onBarClick(item: any) {
    if (!this.isAllZero) this.dataEvent.emit(item);
  }

  getRandomDummyValue(): number {
    const min = 5; // minimum dummy bar height
    const max = 30; // maximum dummy bar height
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}
